import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { LOCALES, type Locale } from '@ebu/contracts';
import { de } from './messages/de';
import { en, type MessageKey, type Messages } from './messages/en';
import { et } from './messages/et';
import { fi } from './messages/fi';

/**
 * UI localisation (en/et/fi/de). Message catalogues are bundled (they are small) and type-checked
 * against the English source. Programme text is localised by the API (`textLocale` tells which
 * language a card is actually in), so this module only covers the interface.
 */

export const MESSAGES: Readonly<Record<Locale, Messages>> = { en, et, fi, de };

/** Language names in their own language, shown in the language selector. */
export const LANGUAGE_ENDONYMS: Readonly<Record<Locale, string>> = {
  en: 'English',
  et: 'Eesti',
  fi: 'Suomi',
  de: 'Deutsch',
};

export type Vars = Readonly<Record<string, string | number>>;

export function interpolate(template: string, vars: Vars = {}): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

type PluralBase = {
  [K in MessageKey]: K extends `${infer B}.one` ? B : never;
}[MessageKey];

export interface Translator {
  locale: Locale;
  t: (key: MessageKey, vars?: Vars) => string;
  /** Picks `<base>.one` / `<base>.other` with Intl.PluralRules; `{count}` is filled in. */
  plural: (base: PluralBase, count: number) => string;
  /** Localised message for a coded error, always followed by "Error CODE." */
  errorMessage: (code: string) => string;
  formatDate: (iso: string) => string;
  /** Names of content languages, e.g. ["et", "en"] → "Eesti, English". */
  languageList: (locales: readonly Locale[]) => string;
}

export function createTranslator(locale: Locale): Translator {
  const messages = MESSAGES[locale];
  const t = (key: MessageKey, vars?: Vars) => interpolate(messages[key], vars);
  // European English conventions (day-month dates) for the English interface.
  const intlLocale = locale === 'en' ? 'en-GB' : locale;
  const rules = new Intl.PluralRules(intlLocale);
  const dates = new Intl.DateTimeFormat(intlLocale, { dateStyle: 'long', timeZone: 'UTC' });
  const list = new Intl.ListFormat(intlLocale, { style: 'short', type: 'unit' });
  return {
    locale,
    t,
    plural: (base, count) => {
      const key = `${base}.${rules.select(count) === 'one' ? 'one' : 'other'}` as MessageKey;
      return t(key, { count });
    },
    errorMessage: (code) => {
      const localisedKey = `error.${code}` as MessageKey;
      const message = localisedKey in messages ? t(localisedKey) : t('error.generic');
      return `${message} ${t('error.code', { code })}`;
    },
    formatDate: (iso) => dates.format(new Date(iso)),
    languageList: (locales) => list.format(locales.map((l) => LANGUAGE_ENDONYMS[l])),
  };
}

const I18nContext = createContext<Translator>(createTranslator('en'));

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const translator = useMemo(() => createTranslator(locale), [locale]);
  return <I18nContext.Provider value={translator}>{children}</I18nContext.Provider>;
}

export function useI18n(): Translator {
  return useContext(I18nContext);
}

/** Best supported UI locale for the browser's preferred languages; English otherwise. */
export function preferredLocale(languages: readonly string[]): Locale {
  for (const tag of languages) {
    const base = tag.toLowerCase().split('-')[0];
    const match = LOCALES.find((l) => l === base);
    if (match) return match;
  }
  return 'en';
}

export type { MessageKey };
